import { branding } from "../config/branding";
import { WORKFLOW_CONFIG, Workflow } from "../config/workflowConfig";
import { Conversation } from "../types/chat";
import { ChatHistoryList } from "./ChatHistoryList";
import { HidLogo } from "./Icons";

interface SidebarProps {
  conversations: Conversation[];
  activeId: string;
  onNewChat: () => void;
  onSelectConversation: (id: string) => void;
  onDeleteConversation?: (id: string) => void;
  onOpenSettings: () => void;
  onOpenAdmin?: () => void;
  onExampleClick: (query: string) => void;
  disabled?: boolean;
}

export function Sidebar({
  conversations,
  activeId,
  onNewChat,
  onSelectConversation,
  onDeleteConversation,
  onOpenSettings,
  onOpenAdmin,
  onExampleClick,
  disabled,
}: SidebarProps) {
  return (
    <aside className="flex w-72 shrink-0 flex-col border-r border-brand-navy/20 bg-brand-navy text-white">
      <div className="border-b border-white/10 p-5">
        <HidLogo className="h-10 w-auto max-w-[180px]" />
        <div className="mt-3">
          <h1 className="text-lg font-bold leading-tight">{branding.appTitle}</h1>
        </div>
        <p className="mt-2 text-sm text-white/70">{branding.appSubtitle}</p>
        <span className="mt-2 inline-block rounded-full bg-white/10 px-2.5 py-0.5 text-xs font-medium text-brand-accent">
          {branding.pocBadge}
        </span>
        <span className="mt-1 block text-[11px] text-white/40">v{branding.appVersion}</span>
      </div>

      <div className="border-b border-white/10 p-3">
        <button
          type="button"
          disabled={disabled}
          onClick={onNewChat}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-accent px-3 py-2.5 text-sm font-semibold text-brand-navy transition hover:bg-brand-accent/90 disabled:opacity-50"
        >
          + New Chat
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        <h2 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-white/50">
          Chat history
        </h2>
        <ChatHistoryList
          conversations={conversations}
          activeId={activeId}
          onSelect={onSelectConversation}
          onDelete={onDeleteConversation}
          disabled={disabled}
        />

        <h2 className="mb-2 mt-5 px-1 text-xs font-semibold uppercase tracking-wider text-white/50">
          Quick examples
        </h2>
        <div className="space-y-2">
          {branding.exampleQueries.slice(0, 3).map((q) => (
            <button
              key={q}
              type="button"
              disabled={disabled}
              onClick={() => onExampleClick(q)}
              className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-left text-xs text-white/80 transition hover:border-brand-accent/50 hover:bg-white/10 disabled:opacity-50"
            >
              {q}
            </button>
          ))}
        </div>
      </div>

      <div className="border-t border-white/10 p-3">
        {onOpenAdmin && (
          <button
            type="button"
            onClick={onOpenAdmin}
            className="mb-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-brand-accent transition hover:bg-white/10"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H2v-2a4 4 0 014-4h3m8-4a4 4 0 100-8 4 4 0 000 8zM9 10a4 4 0 100-8 4 4 0 000 8zm3 5v6m-3-3h6" />
            </svg>
            Manage users
          </button>
        )}
        <button
          type="button"
          onClick={onOpenSettings}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-white/80 transition hover:bg-white/10"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Settings
        </button>
        <p className="mt-2 px-1 text-[10px] leading-relaxed text-white/40">
          Workflows: {(Object.keys(WORKFLOW_CONFIG) as Workflow[]).map((k) => WORKFLOW_CONFIG[k].label).join(" · ")}
        </p>
      </div>
    </aside>
  );
}
